import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  History,
  Image as ImageIcon,
  MessageCirclePlus,
  Paperclip,
  Send,
} from 'lucide-react';
import { Badge, Button, Card, ErrorState, LoadingState } from '@pixlide/ui';
import {
  useAdminMessageAttachmentDownloadUrl,
  useAdminReply,
  useArchivedConversation,
  useArchivedConversations,
  useCloseConversation,
  useConversationAuditLog,
  useConversationDetail,
  useCreateAdminAttachmentUploadTicket,
  useMarkConversationReadAsAdmin,
  useStartNewConversation,
} from '@/api/queries/messages';
import { usePlatformAdmins, useTenantDetail } from '@/api/queries/platform-admin';
import { queryKeys } from '@/api/query-client';
import { createConversationHubConnection } from '@/lib/signalr';
import { AssignmentControl } from '@/features/messages/AssignmentControl';
import type { components } from '@/api/generated/schema';

type MessageResponse = components['schemas']['PixlideApplicationFeaturesSupportMessageResponse'];
type TenantStatus = components['schemas']['PixlideDomainIdentityEnumsTenantStatus'];

const TENANT_STATUS_LABELS: Record<TenantStatus, string> = {
  0: 'Essai',
  1: 'Actif',
  2: 'Suspendu',
  3: 'Résilié',
};

const TENANT_STATUS_TONES: Record<TenantStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  0: 'neutral',
  1: 'success',
  2: 'warning',
  3: 'danger',
};

/** Types MIME acceptés pour les pièces jointes, alignés sur la validation serveur. */
const ALLOWED_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
const MAX_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;

/** Formate une taille de fichier en octets vers un libellé lisible en français ("245 Ko"). */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

/** Initiales d'un nom affiché (tenant ou expéditeur), même pattern que la liste. */
function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
}

/**
 * Détail d'une conversation support : fil de messages + composeur de réponse.
 *
 * Bulles alignées à droite pour les messages de l'admin connecté (`isMine: true`), à gauche
 * pour les messages du client, avec le nom (prénom + nom) de l'utilisateur qui a réellement
 * écrit le message au-dessus des bulles client — une conversation est partagée par tout
 * l'établissement (voir `Conversation.cs`), plusieurs personnes du même tenant peuvent donc
 * y écrire, d'où l'intérêt de les distinguer plutôt que d'afficher un label "Client" générique.
 *
 * Ouvre sa propre connexion SignalR au montage : sur `MessagePosted`, si l'événement
 * concerne CE tenant, invalide le détail (et la liste, pour le badge de non-lus). Marque la
 * conversation comme lue à l'arrivée sur la page.
 */
export function ConversationDetailPage() {
  const { tenantId } = useParams({ from: '/admin/messages/$tenantId' });
  const conversation = useConversationDetail(tenantId);
  const tenant = useTenantDetail(tenantId);
  const adminReply = useAdminReply(tenantId);
  const markRead = useMarkConversationReadAsAdmin(tenantId);
  const closeConversation = useCloseConversation(tenantId);
  const startNewConversation = useStartNewConversation(tenantId);
  const uploadTicket = useCreateAdminAttachmentUploadTicket(tenantId);
  const platformAdmins = usePlatformAdmins();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'active' | 'archive'>('active');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const markedReadRef = useRef(false);

  async function handleFileSelected(file: File) {
    setAttachmentError(null);

    if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
      setAttachmentError('Type de fichier non supporté (images ou PDF uniquement).');
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      setAttachmentError('Fichier trop volumineux (25 Mo maximum).');
      return;
    }

    setIsUploading(true);

    try {
      const ticket = await uploadTicket.mutateAsync({
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
      });

      const uploadResponse = await fetch(ticket.uploadUrl, {
        method: 'PUT',
        headers: ticket.requiredHeaders,
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Échec du dépôt du fichier (${uploadResponse.status}).`);
      }

      const body = draft.trim();

      await adminReply.mutateAsync({
        body: body === '' ? null : body,
        attachmentStorageKey: ticket.storageKey,
        attachmentFileName: file.name,
        attachmentContentType: file.type,
        attachmentSizeBytes: file.size,
      });

      setDraft('');
    } catch (error) {
      setAttachmentError(
        error instanceof Error ? error.message : "Échec de l'envoi de la pièce jointe.",
      );
    } finally {
      setIsUploading(false);
    }
  }

  useEffect(() => {
    if (markedReadRef.current) return;
    markedReadRef.current = true;
    markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    const connection = createConversationHubConnection();

    connection.on('MessagePosted', (message: MessageResponse) => {
      // La connexion admin est abonnée au groupe `platform-admins`, qui reçoit les messages
      // de TOUS les tenants : on ne rafraîchit le détail que si l'événement concerne CETTE
      // conversation. Avant le premier message, `conversationId` est encore `null` côté
      // détail (conversation créée paresseusement) — dans ce cas on invalide quand même,
      // impossible de filtrer plus finement tant qu'elle n'existe pas.
      const matchesThisConversation =
        conversation.data?.conversationId == null || message.conversationId === conversation.data.conversationId;

      if (!matchesThisConversation) return;

      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(tenantId) });
    });

    connection.on('ConversationStatusChanged', () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(tenantId) });
    });

    connection.on('ConversationAssigned', () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversations() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversation(tenantId) });
    });

    void connection.start();

    return () => {
      void connection.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, queryClient]);

  if (conversation.isPending) {
    return <LoadingState label="Chargement de la conversation…" />;
  }

  if (conversation.isError || !conversation.data) {
    return (
      <ErrorState
        message="Impossible de charger cette conversation."
        onRetry={() => void conversation.refetch()}
      />
    );
  }

  const data = conversation.data;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <Link to="/messages" className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900">
        <ArrowLeft aria-hidden className="size-4" />
        Retour aux messages
      </Link>

      {/* Rappel du tenant concerné, visible en permanence (y compris pendant la rédaction
          d'une réponse plus bas) : sans ça, rien sur cette page n'indique à QUEL établissement
          l'admin est en train de répondre — seul l'id figure dans l'URL. */}
      <Link
        to="/tenants/$tenantId"
        params={{ tenantId }}
        className="flex flex-wrap items-center gap-3 rounded-card border border-ink-100 bg-white p-4 transition hover:border-ink-200"
      >
        <span
          aria-hidden
          className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-100 text-brand-700"
        >
          <Building2 className="size-5" strokeWidth={1.5} />
        </span>

        <div className="min-w-0 flex-1">
          {tenant.isPending ? (
            <p className="text-sm text-ink-400">Chargement du tenant…</p>
          ) : null}

          {tenant.isError ? (
            <p className="text-sm text-ink-400">Impossible de charger les informations du tenant.</p>
          ) : null}

          {tenant.data ? (
            <>
              <p className="truncate font-semibold text-ink-900">{tenant.data.companyName}</p>
              <p className="truncate text-xs text-ink-500">
                {tenant.data.slug}
                {tenant.data.city ? ` · ${tenant.data.city}` : ''}
              </p>
            </>
          ) : null}
        </div>

        {tenant.data ? (
          <Badge tone={TENANT_STATUS_TONES[tenant.data.status]}>
            {TENANT_STATUS_LABELS[tenant.data.status]}
          </Badge>
        ) : null}
      </Link>

      <div className="flex items-center gap-1 rounded-pill bg-ink-50/60 p-1 text-sm">
        <button
          type="button"
          onClick={() => setActiveTab('active')}
          className={`rounded-pill px-4 py-1.5 font-medium transition ${
            activeTab === 'active' ? 'bg-white text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-700'
          }`}
        >
          Conversation
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('archive')}
          className={`rounded-pill px-4 py-1.5 font-medium transition ${
            activeTab === 'archive' ? 'bg-white text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-700'
          }`}
        >
          Archives
        </button>
      </div>

      {activeTab === 'active' ? <AuditLogSection tenantId={tenantId} /> : null}

      {activeTab === 'archive' ? <ArchiveTab tenantId={tenantId} /> : null}

      {activeTab === 'active' ? (
      <Card className="flex flex-col gap-4 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {data.isClosed ? <Badge tone="neutral">Fermée</Badge> : <Badge tone="success">Ouverte</Badge>}

            {data.conversationId ? (
              <span
                title={data.conversationId}
                className="rounded-pill bg-ink-50 px-2.5 py-1 font-mono text-[11px] text-ink-400"
              >
                #{data.conversationId.slice(0, 8)}
              </span>
            ) : null}

            <AssignmentControl
              tenantId={tenantId}
              assignedAdminUserId={data.assignedAdminUserId ?? null}
              assignedAdminName={data.assignedAdminName ?? null}
              admins={platformAdmins.data?.items ?? []}
            />
          </div>

          {!data.isClosed && data.conversationId ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              isLoading={closeConversation.isPending}
              onClick={() => closeConversation.mutate()}
            >
              <CheckCircle2 aria-hidden className="size-4" />
              Fermer la conversation
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col gap-4 rounded-card bg-ink-50/40 p-4">
          {data.messages.length === 0 ? (
            <p className="py-8 text-center text-sm text-ink-500">
              Aucun message pour l'instant — écrivez le premier ci-dessous.
            </p>
          ) : null}

          {data.messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
        </div>

        {attachmentError ? (
          <p className="text-sm text-red-600" role="alert">
            {attachmentError}
          </p>
        ) : null}

        {data.isClosed ? (
          <div className="flex flex-col items-center gap-3 border-t border-ink-100/70 pt-4 text-center">
            <p className="text-sm text-ink-500">
              Cette conversation est fermée. Démarrez une nouvelle conversation pour continuer à échanger avec ce
              tenant.
            </p>

            <Button
              type="button"
              isLoading={startNewConversation.isPending}
              onClick={() => startNewConversation.mutate()}
            >
              <MessageCirclePlus aria-hidden className="size-4" />
              Nouvelle conversation
            </Button>

            {startNewConversation.isError ? (
              <p className="text-xs text-red-600" role="alert">
                Impossible de démarrer une nouvelle conversation.
              </p>
            ) : null}
          </div>
        ) : (
          <form
            className="flex items-end gap-2 border-t border-ink-100/70 pt-4"
            onSubmit={(event) => {
              event.preventDefault();
              const body = draft.trim();
              if (body === '') return;

              adminReply.mutate(
                { body },
                {
                  onSuccess: () => setDraft(''),
                },
              );
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) void handleFileSelected(file);
              }}
            />

            <Button
              type="button"
              variant="ghost"
              isLoading={isUploading}
              disabled={isUploading || adminReply.isPending}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Joindre un fichier"
            >
              <Paperclip aria-hidden className="size-4" />
            </Button>

            <textarea
              aria-label="Répondre au tenant"
              placeholder="Écrire une réponse…"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={2}
              className="h-16 flex-1 resize-none rounded-2xl bg-ink-50/60 px-4 py-2.5 text-sm ring-1 ring-ink-100 placeholder:text-ink-300 focus:ring-brand-500"
            />

            <Button
              type="submit"
              isLoading={adminReply.isPending}
              disabled={draft.trim() === '' || isUploading}
            >
              <Send aria-hidden className="size-4" />
              Envoyer
            </Button>
          </form>
        )}
      </Card>
      ) : null}
    </div>
  );
}

const AUDIT_ACTION_LABELS: Record<string, (entry: { actorName?: string | null; targetName?: string | null }) => string> = {
  Started: () => 'Conversation démarrée',
  Closed: () => 'Conversation fermée',
  Assigned: (entry) =>
    `Assignée à ${entry.targetName ?? 'un administrateur'} par ${entry.actorName ?? 'un administrateur'}`,
  Unassigned: (entry) => `Désassignée par ${entry.actorName ?? 'un administrateur'}`,
};

/**
 * Journal du cycle de vie de la conversation (démarrage, fermeture, (dés)assignation), replié
 * par défaut — historique consulté ponctuellement, pas une information à afficher en continu.
 */
function AuditLogSection({ tenantId }: { tenantId: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const auditLog = useConversationAuditLog(tenantId);

  return (
    <Card className="flex flex-col gap-3 p-4">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="flex items-center justify-between gap-2 text-left text-sm font-medium text-ink-700"
      >
        <span className="flex items-center gap-2">
          <History aria-hidden className="size-4" />
          Historique de la conversation
        </span>
        <ChevronDown aria-hidden className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen ? (
        <div className="flex flex-col gap-2 border-t border-ink-100/70 pt-3">
          {auditLog.isPending ? <p className="text-xs text-ink-400">Chargement…</p> : null}

          {auditLog.isError ? (
            <p className="text-xs text-red-600" role="alert">
              Impossible de charger l'historique.
            </p>
          ) : null}

          {auditLog.data && auditLog.data.length === 0 ? (
            <p className="text-xs text-ink-400">Aucun événement pour l'instant.</p>
          ) : null}

          {auditLog.data && auditLog.data.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {auditLog.data.map((entry) => {
                const label = AUDIT_ACTION_LABELS[entry.action]?.(entry) ?? entry.action;

                return (
                  <li key={entry.id} className="flex items-center justify-between gap-4 text-xs text-ink-600">
                    <span>{label}</span>
                    <span className="shrink-0 text-ink-400">
                      {new Date(entry.createdAt).toLocaleString('fr-FR')}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

const ARCHIVE_PAGE_SIZE = 10;

/**
 * Onglet « Archives » : liste paginée des conversations fermées du tenant, avec ouverture du
 * détail en lecture seule dans la même carte (pas de nouvelle route — état local uniquement).
 */
function ArchiveTab({ tenantId }: { tenantId: string }) {
  const [page, setPage] = useState(1);
  const [openedConversationId, setOpenedConversationId] = useState<string | null>(null);

  const archivedList = useArchivedConversations(tenantId, page, ARCHIVE_PAGE_SIZE);

  if (openedConversationId) {
    return (
      <ArchivedConversationDetail
        conversationId={openedConversationId}
        onBack={() => setOpenedConversationId(null)}
      />
    );
  }

  if (archivedList.isPending) {
    return <LoadingState label="Chargement des conversations archivées…" />;
  }

  if (archivedList.isError || !archivedList.data) {
    return (
      <ErrorState
        message="Impossible de charger les conversations archivées."
        onRetry={() => void archivedList.refetch()}
      />
    );
  }

  const data = archivedList.data;

  return (
    <Card className="flex flex-col gap-4 p-6">
      {data.items.length === 0 ? (
        <p className="py-8 text-center text-sm text-ink-500">Aucune conversation archivée.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-ink-100/70">
          {data.items.map((item) => (
            <li key={item.conversationId}>
              <button
                type="button"
                onClick={() => setOpenedConversationId(item.conversationId)}
                className="flex w-full items-center justify-between gap-4 py-3 text-left hover:bg-ink-50/60"
              >
                <span className="min-w-0 flex-1 truncate text-sm text-ink-700">
                  {item.lastMessagePreview ?? '—'}
                </span>

                <span className="shrink-0 text-xs text-ink-400">
                  {item.closedAt ? `Fermée le ${new Date(item.closedAt).toLocaleString('fr-FR')}` : '—'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {data.items.length > 0 ? (
        <div className="flex items-center justify-between border-t border-ink-100/70 pt-3 text-sm text-ink-500">
          <span>
            Page {data.page} sur {Math.max(data.totalPages, 1)} · {data.totalCount} conversation
            {data.totalCount > 1 ? 's' : ''}
          </span>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!data.hasPreviousPage}
              onClick={() => setPage((current) => current - 1)}
              aria-label="Page précédente"
              className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft aria-hidden className="size-4" />
            </button>

            <button
              type="button"
              disabled={!data.hasNextPage}
              onClick={() => setPage((current) => current + 1)}
              aria-label="Page suivante"
              className="grid size-8 place-items-center rounded-pill ring-1 ring-ink-100 transition hover:bg-ink-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronRight aria-hidden className="size-4" />
            </button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

/** Détail en lecture seule d'une conversation archivée : fil de messages, sans composeur. */
function ArchivedConversationDetail({
  conversationId,
  onBack,
}: {
  conversationId: string;
  onBack: () => void;
}) {
  const conversation = useArchivedConversation(conversationId);

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900"
        >
          <ArrowLeft aria-hidden className="size-4" />
          Retour aux archives
        </button>

        <div className="flex items-center gap-2">
          <span
            title={conversationId}
            className="rounded-pill bg-ink-50 px-2.5 py-1 font-mono text-[11px] text-ink-400"
          >
            #{conversationId.slice(0, 8)}
          </span>
          <Badge tone="neutral">Fermée</Badge>
        </div>
      </div>

      {conversation.isPending ? <LoadingState label="Chargement de la conversation…" /> : null}

      {conversation.isError || (!conversation.isPending && !conversation.data) ? (
        <ErrorState
          message="Impossible de charger cette conversation."
          onRetry={() => void conversation.refetch()}
        />
      ) : null}

      {conversation.data ? (
        <div className="flex flex-col gap-4 rounded-card bg-ink-50/40 p-4">
          {conversation.data.messages.length === 0 ? (
            <p className="py-8 text-center text-sm text-ink-500">Aucun message dans cette conversation.</p>
          ) : null}

          {conversation.data.messages.map((message) => (
            <MessageBubble key={message.id} message={message} />
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function MessageBubble({ message }: { message: MessageResponse }) {
  const isMine = message.isMine;
  const downloadUrl = useAdminMessageAttachmentDownloadUrl();
  const senderLabel = message.senderName?.trim() || 'Client';

  return (
    <div className={`flex flex-col gap-1 ${isMine ? 'items-end' : 'items-start'}`}>
      {!isMine ? (
        <span className="flex items-center gap-1.5 px-1 text-xs font-medium text-ink-500">
          <span
            aria-hidden
            className="grid size-5 shrink-0 place-items-center rounded-full bg-ink-100 text-[10px] font-semibold text-ink-700"
          >
            {initials(senderLabel)}
          </span>
          {senderLabel}
        </span>
      ) : null}

      <div
        className={`flex max-w-md flex-col gap-2 rounded-2xl px-4 py-2.5 text-sm ${
          isMine ? 'bg-brand-500 text-white' : 'border border-ink-100 bg-white text-ink-900 shadow-card'
        }`}
      >
        {message.body ? <p className="whitespace-pre-wrap">{message.body}</p> : null}

        {message.hasAttachment ? (
          <button
            type="button"
            disabled={downloadUrl.isPending}
            onClick={() => {
              downloadUrl.mutate(message.id, {
                onSuccess: (data) => {
                  window.open(data.downloadUrl, '_blank');
                },
              });
            }}
            className={`flex items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs transition ${
              isMine ? 'bg-white/15 hover:bg-white/25' : 'bg-ink-50 hover:bg-ink-100'
            } disabled:cursor-not-allowed disabled:opacity-70`}
          >
            {message.attachmentContentType === 'application/pdf' ? (
              <FileText aria-hidden className="size-4 shrink-0" />
            ) : (
              <ImageIcon aria-hidden className="size-4 shrink-0" />
            )}
            <span className="min-w-0 flex-1 truncate">{message.attachmentFileName ?? 'Pièce jointe'}</span>
            {message.attachmentSizeBytes != null ? (
              <span className="shrink-0 opacity-70">{formatFileSize(message.attachmentSizeBytes)}</span>
            ) : null}
          </button>
        ) : null}

        {downloadUrl.isError ? (
          <p className={`text-xs ${isMine ? 'text-white' : 'text-red-600'}`} role="alert">
            Impossible de récupérer le fichier.
          </p>
        ) : null}
      </div>

      <span className="px-1 text-xs text-ink-300">
        {new Date(message.createdAt).toLocaleString('fr-FR')}
      </span>
    </div>
  );
}
